import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Lock, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Site Access Gate — "Cinematic Teaser" redesign.
 *
 * Comportamento invariato rispetto alla versione precedente:
 * - Al mount chiama /api/site-access/status
 * - Se required && !authorized -> mostra il gate
 * - Se password corretta -> cookie HttpOnly server-side, poi render children
 * - Esente: il gate NON wrappa le route /admin (fatto in App.js)
 *
 * Design:
 * - Split 60/40 desktop, stack mobile (solo form su mobile)
 * - Entrance animazioni staggered (framer-motion)
 * - Aurora blob animato + grain texture
 * - Success animation: fade-out + zoom-in prima di rivelare children
 * - Rispetta prefers-reduced-motion
 */
export default function SiteAccessGate({ children }) {
  const [status, setStatus] = useState({ loading: true, required: false, authorized: true });
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    let ignore = false;
    axios.get(`${API}/site-access/status`, { withCredentials: true })
      .then(({ data }) => { if (!ignore) setStatus({ loading: false, ...data }); })
      .catch(() => { if (!ignore) setStatus({ loading: false, required: false, authorized: true }); });
    return () => { ignore = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API}/site-access/verify`, { password: pwd }, { withCredentials: true });
      setUnlocking(true);
      // Piccolo delay per la success animation prima di svelare i children
      setTimeout(() => {
        setStatus(s => ({ ...s, authorized: true }));
      }, reduced ? 0 : 700);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Password errata");
    } finally {
      setSubmitting(false);
    }
  };

  if (status.loading) {
    return (
      <div className="min-h-screen bg-[#0A1912] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#D97B58]" />
      </div>
    );
  }

  if (!status.required || status.authorized) {
    return children;
  }

  // Motion helpers (rispettano prefers-reduced-motion)
  const fadeUp = (delay = 0) => reduced
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, transition: { duration: 0 } }
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <AnimatePresence mode="wait">
      {!unlocking && (
        <motion.div
          key="gate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-gate-title"
          data-testid="site-access-gate"
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.03, filter: "blur(6px)" }}
          transition={{ duration: reduced ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="min-h-screen bg-[#0A1912] text-white relative overflow-hidden"
        >
          {/* Aurora animata */}
          {!reduced && (
            <>
              <motion.div
                aria-hidden="true"
                className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full bg-[#D97B58]/25 blur-[140px] pointer-events-none"
                animate={{ x: [0, 40, -20, 0], y: [0, 30, -10, 0], scale: [1, 1.08, 0.96, 1] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                aria-hidden="true"
                className="absolute -bottom-60 -left-40 w-[640px] h-[640px] rounded-full bg-[#1F4530]/60 blur-[140px] pointer-events-none"
                animate={{ x: [0, -30, 20, 0], y: [0, -40, 20, 0], scale: [1, 1.05, 0.98, 1] }}
                transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
              />
            </>
          )}

          {/* Grain texture */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
            }}
          />

          {/* Layout split */}
          <div className="relative min-h-screen grid lg:grid-cols-5 px-6 sm:px-10 lg:px-16 py-10 lg:py-14">

            {/* LEFT PANEL — brand & story */}
            <div
              className="lg:col-span-3 hidden lg:flex flex-col justify-between pr-10 xl:pr-16"
              data-testid="site-gate-left-panel"
            >
              <div>
                <motion.img
                  {...fadeUp(0)}
                  src="/logo_bianco.png"
                  alt="Colucci Assicuratori"
                  className="h-8 w-auto object-contain opacity-90"
                />
              </div>

              <div className="max-w-2xl">
                <motion.div {...fadeUp(0.05)} className="mb-8">
                  <span
                    className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#D97B58]/40 bg-[#D97B58]/8 text-[11px] uppercase tracking-[0.3em] text-[#F5EDD8]/90 font-medium"
                    data-testid="site-gate-live-badge"
                  >
                    <span className="relative flex w-2 h-2">
                      {!reduced && (
                        <span className="absolute inline-flex w-full h-full rounded-full bg-[#D97B58] opacity-75 animate-ping" />
                      )}
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-[#D97B58]" />
                    </span>
                    Anteprima · 2026
                  </span>
                </motion.div>

                <motion.h1
                  {...fadeUp(0.15)}
                  id="site-gate-title"
                  className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#F5EDD8] font-medium"
                  data-testid="site-gate-title"
                >
                  Colucci<br />Assicuratori
                </motion.h1>

                <motion.p
                  {...fadeUp(0.28)}
                  className="mt-7 text-lg text-white/70 leading-relaxed max-w-lg"
                  data-testid="site-gate-tagline"
                >
                  Consulenza assicurativa a Napoli dal <strong className="text-[#F5EDD8] font-semibold">1982</strong>.
                  <br />
                  Tra pochi giorni <span className="text-[#D97B58]">online</span>.
                </motion.p>

                <motion.div
                  {...fadeUp(0.42)}
                  className="mt-10 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/50"
                  data-testid="site-gate-trust-chips"
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#D97B58]" />
                    Multi-compagnia
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#D97B58]" />
                    Agenzia indipendente
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#D97B58]" />
                    Rete filiali
                  </span>
                </motion.div>
              </div>

              <motion.p
                {...fadeUp(0.6)}
                className="text-[11px] text-white/30 tracking-wide"
                data-testid="site-gate-bottom-bar"
              >
                Colucci Assicuratori · Napoli · dal 1982 · IVASS Sez. A
              </motion.p>
            </div>

            {/* RIGHT PANEL — form card */}
            <div className="lg:col-span-2 flex flex-col justify-center min-h-[80vh] lg:min-h-0">
              {/* Mobile-only brand header */}
              <div className="lg:hidden mb-10 text-center">
                <motion.img
                  {...fadeUp(0)}
                  src="/logo_bianco.png"
                  alt="Colucci Assicuratori"
                  className="h-7 w-auto object-contain mx-auto opacity-90 mb-5"
                />
                <motion.span
                  {...fadeUp(0.05)}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D97B58]/40 text-[10px] uppercase tracking-[0.3em] text-[#F5EDD8]/90"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D97B58] animate-pulse" />
                  Anteprima · 2026
                </motion.span>
                <motion.h1
                  {...fadeUp(0.15)}
                  className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#F5EDD8] font-medium mt-5"
                >
                  Colucci Assicuratori
                </motion.h1>
                <motion.p
                  {...fadeUp(0.25)}
                  className="mt-3 text-sm text-white/60 leading-relaxed max-w-xs mx-auto"
                >
                  Consulenza assicurativa a Napoli dal 1982.
                  Tra pochi giorni online.
                </motion.p>
              </div>

              <motion.form
                initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                animate={
                  err && !reduced
                    ? { opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }
                    : { opacity: 1, y: 0 }
                }
                transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onSubmit={submit}
                className="relative w-full max-w-sm mx-auto lg:mx-0 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-7 sm:p-8"
                data-testid="site-access-form"
              >
                <div className="flex items-center gap-3 mb-6">
                  <motion.div
                    animate={reduced ? undefined : { y: [0, -3, 0] }}
                    transition={reduced ? undefined : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    className="shrink-0 w-11 h-11 rounded-xl bg-[#D97B58]/15 border border-[#D97B58]/30 flex items-center justify-center"
                  >
                    <Lock className="w-4.5 h-4.5 text-[#D97B58]" strokeWidth={2} />
                  </motion.div>
                  <div>
                    <h2 className="font-heading text-lg text-[#F5EDD8] font-medium leading-tight">
                      Accedi all&apos;anteprima
                    </h2>
                    <p className="text-xs text-white/50 mt-0.5">Inserisci la password fornita</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="password"
                      value={pwd}
                      onChange={e => { setPwd(e.target.value); if (err) setErr(""); }}
                      placeholder="Password"
                      autoComplete="current-password"
                      autoFocus
                      required
                      className="peer w-full h-12 px-4 rounded-xl bg-black/25 border border-white/10 text-white text-sm placeholder:text-white/25 focus:border-[#D97B58] focus:bg-black/40 focus:outline-none focus:ring-2 focus:ring-[#D97B58]/30 transition-all"
                      data-testid="site-access-password-input"
                      aria-invalid={err ? "true" : "false"}
                      aria-describedby={err ? "site-gate-error" : undefined}
                    />
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-xl opacity-0 peer-focus:opacity-100 transition-opacity ring-4 ring-[#D97B58]/10"
                    />
                  </div>

                  {err && (
                    <motion.p
                      initial={reduced ? { opacity: 1 } : { opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      id="site-gate-error"
                      role="alert"
                      className="text-sm text-red-300/90 text-center"
                      data-testid="site-access-error"
                    >
                      {err}
                    </motion.p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={submitting || !pwd}
                    whileHover={reduced || submitting || !pwd ? undefined : { scale: 1.01 }}
                    whileTap={reduced || submitting || !pwd ? undefined : { scale: 0.99 }}
                    className="group relative w-full h-12 rounded-xl bg-[#D97B58] text-white text-sm font-semibold tracking-wide hover:bg-[#C06A48] disabled:opacity-50 disabled:cursor-not-allowed transition-colors overflow-hidden flex items-center justify-center gap-2"
                    data-testid="site-access-submit-btn"
                  >
                    {!reduced && !submitting && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 rounded-xl bg-[#D97B58] blur-md opacity-40 -z-10 group-hover:opacity-70 transition-opacity"
                      />
                    )}
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <span>Entra</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </motion.button>
                </div>

                <div className="mt-6 pt-5 border-t border-white/5 flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-white/40 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-white/40 leading-relaxed">
                    Sito riservato ai clienti e ai partner professionali.
                    <br />
                    Accesso limitato fino al lancio ufficiale.
                  </p>
                </div>
              </motion.form>

              <motion.p
                {...fadeUp(0.6)}
                className="lg:hidden mt-8 text-[11px] text-white/30 text-center tracking-wide"
              >
                Colucci Assicuratori · Napoli · dal 1982 · IVASS Sez. A
              </motion.p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
